import { useEffect, useRef } from 'react';
import { useQuizGameState } from '@mentimeter/quiz';
import { Text } from '@mentimeter/ragnar-ui/text';
import { GameStateEnums, type InitialGameStates } from '@mentimeter/quiz-types';
import { useTranslations } from 'next-intl';
import { CenteringContainer } from '../../shared/components/quiz/CenteringContainer';
import {
  QuizLeaderboardVisualization,
  NoResults,
} from './QuizLeaderboardVisualization';
import type { useLeaderboardResults } from './useLeaderboardResults';

const LEADERBOARD_THRESHOLD = 10;

export const LivePresentation = ({
  initialGameState,
  activeSession,
  timeBasedScoring,
  questionId,
  isPreviewResults = false,
  leaderboardResults,
  shouldAnimate,
}: {
  initialGameState: InitialGameStates;
  activeSession: number;
  timeBasedScoring: boolean;
  questionId: string;
  isPreviewResults?: boolean;
  leaderboardResults: ReturnType<typeof useLeaderboardResults>;
  shouldAnimate: boolean;
}) => {
  const { gameState, createQuiz, changePlayerName } = useQuizGameState();
  const t = useTranslations('presenting.quiz');

  const prevActiveSession = useRef(activeSession);
  useEffect(() => {
    if (isPreviewResults) {
      return;
    }
    const sessionChanged = prevActiveSession.current !== activeSession;
    if (!gameState || sessionChanged) {
      createQuiz(initialGameState);
      prevActiveSession.current = activeSession;
    }
  }, [
    createQuiz,
    initialGameState,
    activeSession,
    gameState,
    isPreviewResults,
  ]);

  const numberOfPlayers = leaderboardResults?.length ?? 0;

  if (
    !leaderboardResults ||
    (leaderboardResults && leaderboardResults.length === 0)
  ) {
    return (
      <CenteringContainer>
        <NoResults />
      </CenteringContainer>
    );
  }

  if (isPreviewResults) {
    return (
      <CenteringContainer>
        <QuizLeaderboardVisualization
          id={`preview-results-question-${questionId}`}
          key={`preview-results-question-${questionId}`}
          results={leaderboardResults}
          enableAnimations={shouldAnimate}
          timeBasedScoring={timeBasedScoring}
          revealWinner={initialGameState === GameStateEnums.FINAL_LEADERBOARD}
          onPlayerNameClick={changePlayerName}
        />
      </CenteringContainer>
    );
  }

  return (
    <>
      <CenteringContainer>
        <QuizLeaderboardVisualization
          id={questionId}
          results={leaderboardResults}
          enableAnimations={shouldAnimate}
          timeBasedScoring={timeBasedScoring}
          revealWinner={initialGameState === GameStateEnums.FINAL_LEADERBOARD}
          onPlayerNameClick={changePlayerName}
        />
      </CenteringContainer>
      {numberOfPlayers > LEADERBOARD_THRESHOLD && (
        <Text
          mb={5}
          textAlign="center"
          color="text"
          fontWeight="semiBold"
          fontSize="200"
        >
          {t('leaderboard.total_players_message', {
            numberOfPlayers,
          })}
        </Text>
      )}
    </>
  );
};
